import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { defaults } from 'lodash';

export interface ProjectResponse<T = any> {
  data: T;
  headers: (name: string) => string | null;
}

@Injectable({ providedIn: 'root' })
export class ProjectService {
  constructor(
    private http: HttpClient,
    @Inject('$state') private $state: any,
  ) {}

  find(id: string | number): Promise<ProjectResponse> {
    return firstValueFrom(this.http.get<any>(`/api/v1/projects/${id}`, { observe: 'response' }))
      .then(response => this.toResponse(response, this.addItemUrl(response.body)));
  }

  random(): Promise<ProjectResponse> {
    return firstValueFrom(this.http.get<any>('/api/v1/projects/random', { observe: 'response' }))
      .then(response => this.toResponse(response, this.addItemUrls(response.body)));
  }

  feed(parameters?: any): Promise<ProjectResponse> {
    const params = defaults({ ...(parameters || {}) }, { page: 1, per_page: 10, filter: { state: 'published' } });

    return firstValueFrom(
      this.http.get<any>('/api/v1/projects', { observe: 'response', params: this.buildParams(params) }),
    ).then(response => this.toResponse(response, this.addItemUrls(response.body)));
  }

  create(project: any): Promise<ProjectResponse> {
    return firstValueFrom(this.http.post<any>('/api/v1/projects', project, { observe: 'response' }))
      .then(response => this.toResponse(response, response.body));
  }

  apply(id: number): Promise<ProjectResponse> {
    return firstValueFrom(this.http.post<any>(`/api/v1/projects/${id}/apply`, '', { observe: 'response' }))
      .then(response => this.toResponse(response, response.body));
  }

  info(): Promise<ProjectResponse> {
    return firstValueFrom(this.http.get<any>('/api/v1/projects/info', { observe: 'response' }))
      .then(response => this.toResponse(response, response.body));
  }

  private toResponse(response: HttpResponse<any>, data: any): ProjectResponse {
    return { data, headers: (name: string) => response.headers.get(name) };
  }

  private addItemUrl(item: any): any {
    if (item?.type === 'project') {
      item.url = this.$state.href('singleproject', { projectId: item.slug }, { absolute: true });
    }
    return item;
  }

  private addItemUrls(items: any): any {
    return Array.isArray(items) ? items.map(item => this.addItemUrl(item)) : items;
  }

  // Mirrors the AngularJS $http serializer: objects are JSON-encoded, arrays repeat the key.
  private buildParams(obj: Record<string, any>): HttpParams {
    return Object.keys(obj).reduce((params, key) => {
      const val = obj[key];
      if (val === null || val === undefined) {
        return params;
      }
      if (Array.isArray(val)) {
        return val.reduce((p, v) => p.append(key, typeof v === 'object' ? JSON.stringify(v) : String(v)), params);
      }
      return params.set(key, typeof val === 'object' ? JSON.stringify(val) : String(val));
    }, new HttpParams());
  }
}
